import React from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Calendar } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';

/**
 * Composant réutilisable pour filtrer par plage de dates
 * Utilisation:
 * <DateRangeFilter
 *   startDate={filters.start_date}
 *   endDate={filters.end_date}
 *   onStartDateChange={(date) => setFilters({...filters, start_date: date})}
 *   onEndDateChange={(date) => setFilters({...filters, end_date: date})}
 *   onReset={() => setFilters({...filters, start_date: '', end_date: ''})}
 *   showQuickFilters={true}
 * />
 */
export function DateRangeFilter({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onReset,
  showQuickFilters = true,
  className = ''
}) {
  const setDateRange = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case 'today':
        start = end = format(today, 'yyyy-MM-dd');
        break;
      case 'yesterday':
        start = end = format(subDays(today, 1), 'yyyy-MM-dd');
        break;
      case 'week':
        start = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        end = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case 'month':
        start = format(startOfMonth(today), 'yyyy-MM-dd');
        end = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'all':
      default:
        start = '';
        end = '';
        break;
    }

    onStartDateChange(start);
    onEndDateChange(end);
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const hasFilter = startDate || endDate;

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <Calendar className="w-4 h-4 text-muted-foreground" />
      
      {showQuickFilters && (
        <>
          <div className="flex items-center gap-1">
            <Button
              variant={startDate === today && endDate === today ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange('today')}
            >
              Aujourd'hui
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDateRange('week')}
            >
              Semaine
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDateRange('month')}
            >
              Mois
            </Button>
          </div>
          <div className="h-6 w-px bg-border hidden sm:block" />
        </>
      )}

      <div className="flex items-center gap-2">
        <Label className="text-sm whitespace-nowrap">Du</Label>
        <Input
          type="date"
          value={startDate || ''}
          onChange={(e) => onStartDateChange(e.target.value)}
          className="w-[140px]"
        />
        <Label className="text-sm whitespace-nowrap">au</Label>
        <Input
          type="date"
          value={endDate || ''}
          onChange={(e) => onEndDateChange(e.target.value)}
          className="w-[140px]"
        />
      </div>

      {hasFilter && onReset && (
        <Button variant="ghost" size="sm" onClick={onReset}>
          Réinitialiser
        </Button>
      )}
    </div>
  );
}

export default DateRangeFilter;
